import React, { FC, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { currency } from '../../helpers/currency';

import TopUpTransactionDetails from './TopUpTransactionDetails';
import PurchaseTransactionDetails from './PurchaseTransactionDetails';
import RefundTransactionDetails from './RefundTransactionDetails';

import { Button, FlexBox, Loader, Spinner } from 'components';
import { Order, useData, useOrders } from 'data';
import useMediaQuery from 'hooks/mediaQuery/useMediaQuery';
import { TopUpType, isDefaultTopUpType } from 'helpers/topUpTypes';
import { selectEventCurrency } from 'store';

type TransactionType = 'purchase' | 'refund' | 'topup';

interface TransactionDetailsProps {
  order: Order;
  vendorName: string;
  currency: currency | undefined;
}

const TransactionDetails: FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const [searchParams] = useSearchParams();
  const { sendEmailReceipt, isOrdersLoading } = useOrders();
  const { securedApi } = useData();
  const navigate = useNavigate();
  const isMobile = useMediaQuery();
  const currency = useSelector(selectEventCurrency);

  const [order, setOrder] = useState<Order | undefined>();
  const [vendorName, setVendorName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const transactionType = useMemo<TransactionType>(() => {
    const typeFromParams = searchParams.get('type');
    if (typeFromParams === 'purchase') return 'purchase';
    if (!order) return 'purchase';

    if (order.refunds) return 'refund';
    if (order.products[0]?.productKind === 'token') return 'topup';
    return 'purchase';
  }, [searchParams, order]);

  const shouldShowEmailReceipt = useMemo(() => {
    if (!order) return false;

    const topUpType = order.topupTypeUuid || '';
    const isTopUp = transactionType === 'topup';

    return (
      !isTopUp ||
      (isDefaultTopUpType(topUpType) &&
        topUpType !== TopUpType.ADMIN_TOPUP &&
        topUpType !== TopUpType.CUSTOM_TOPUP)
    );
  }, [order, transactionType]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const order = await securedApi?.OrdersApi.getOrderById(transactionId!);

        if (!order) {
          throw new Error('Transaction not found');
        }

        setOrder(order);

        if (order.folioId && order.products[0]?.productKind !== 'token') {
          try {
            const vendorInfo = await securedApi?.OrdersApi.getVendorByFolioId(order.folioId!);
            setVendorName(
              vendorInfo?.vendorName || order.misc?.sessionInfo?.locationName || 'Intellitix',
            );
          } catch (vendorError) {
            console.warn('Failed to fetch vendor info:', vendorError);
            setVendorName(order.misc?.sessionInfo?.locationName || 'Intellitix');
          }
        }
      } catch (err) {
        console.error('Failed to fetch transaction details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transaction details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderDetails();
  }, [transactionId, securedApi]);

  const handleGoBack = () => navigate(-1);
  const handleEmailReceipt = () => order && sendEmailReceipt(order.uuid);

  const renderDetailsComponent = () => {
    if (!order) return null;

    const detailsProps: TransactionDetailsProps = {
      order,
      vendorName,
      currency,
    };

    switch (transactionType) {
      case 'purchase':
        return <PurchaseTransactionDetails {...detailsProps} />;
      case 'refund':
        return <RefundTransactionDetails {...detailsProps} />;
      case 'topup':
        return <TopUpTransactionDetails order={order} />;
      default:
        return <PurchaseTransactionDetails {...detailsProps} />;
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center h-screen p-4'>
        <p className='text-red-500 mb-4'>{error}</p>
        <Button onClick={handleGoBack} className='btn btn-normal btn-primary'>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames('px-4 pt-2', isMobile && 'bg-white')}>
      <div className='mb-20'>
        <FlexBox
          className={classNames('flex mt-[4px] items-center leading-10', {
            '!justify-start': isMobile,
          })}
        >
          {isMobile && (
            <div className='pl-1 pr-4 cursor-pointer' onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} size='lg' />
            </div>
          )}
          <p className='text-[19px] font-semibold'>Transaction Details</p>

          {!isMobile && (
            <div className='ml-auto flex items-center gap-2'>
              <Button className='btn btn-normal btn-secondary' onClick={handleGoBack}>
                Back
              </Button>
              {shouldShowEmailReceipt && (
                <Button
                  className='btn btn-normal btn-secondary'
                  onClick={handleEmailReceipt}
                  isLoading={isOrdersLoading}
                  disabled={isOrdersLoading}
                >
                  Email Receipt
                </Button>
              )}
            </div>
          )}
        </FlexBox>

        {renderDetailsComponent()}
      </div>
      {isMobile && shouldShowEmailReceipt && (
        <div className='fixed bottom-0 left-0 w-full bg-white shadow-lg p-4 rounded-2xl space-y-2'>
          <Button
            className={classNames('w-full btn-primary rounded-lg py-2', {
              'bg-intelli-primaryNew': isOrdersLoading,
            })}
            onClick={handleEmailReceipt}
            disabled={isOrdersLoading}
          >
            {isOrdersLoading ? <Spinner size={6} color='gray' /> : 'Email Receipt'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TransactionDetails;
